import humps from 'humps';
import { getAPIUrl, getHeaders, responseHandler } from '../utils';
import { StateCreator } from 'zustand';
import { MerqResponse, SubStore } from 'store/types';
import {
  OptionStrikeConfig,
  SelfFinancingStrategy,
} from 'pages/IndexWizard/DefinedOutcomeForm/types';

const API_URL = getAPIUrl();

export interface VariableLegParamsRequest {
  exerciseStyle: 'EUROPEAN' | 'AMERICAN';
  exposure: number;
  optionType: 'PUT' | 'CALL';
  root: string;
  strikeType: 'DELTA' | 'PCT_SPOT';
  strikeValue: number;
  tradeType: 'BUY' | 'SELL';
}

export interface DefinedOutcomeSolverRequest {
  root: string;
  expiry: {
    expiry_period_type: string;
    expiry_period_value: number;
  };
  options: OptionStrikeConfig[];
  variable_leg_strategy: SelfFinancingStrategy;
  // variable_leg_params: VariableLegParamsRequest;
}

export interface OptionResponse {
  bidAskSpread: number;
  delta: number;
  dividendYield: number;
  gamma: number;
  interestRate: number;
  price: number;
  spot: number;
  theta: number;
  vega: number;
  vol: number;
}

export interface VariableLegResponse extends VariableLegParamsRequest {
  price: number;
}

export interface DefinedOutcomeSolverResponse {
  optionSolutions: OptionResponse[];
  variableLegCashSolution: number;
  variableLegSolution: VariableLegResponse;
}

export interface DefinedOutcomeSolverSlice extends SubStore {
  value?: DefinedOutcomeSolverResponse;

  get: (
    request: DefinedOutcomeSolverRequest
  ) => Promise<MerqResponse<DefinedOutcomeSolverResponse>>;
}

export const createDefinedOutcomeSolverSlice: StateCreator<
  DefinedOutcomeSolverSlice,
  [],
  [],
  DefinedOutcomeSolverSlice
> = (setState, getState) => ({
  hash: '',
  status: 'init',
  code: 0,
  get: async request => {
    const hash = getState().hash;
    const requestHash = JSON.stringify(request);
    if (hash === requestHash) {
      return Promise.resolve({
        content: getState().value!,
        errors: [],
        status: 'success' as const,
        code: 200,
      });
    }
    setState({ status: 'loading' });

    const headers = await getHeaders('POST');
    const body = humps.decamelizeKeys(request);

    const response = await responseHandler<DefinedOutcomeSolverResponse>(
      fetch(`${API_URL}/helper/solver/defined_outcome`, {
        ...headers,
        body: JSON.stringify(body),
      }),
      err => `Couldn't calculate prices. (${err})`
    );

    if (!response.errors.length) {
      setState({
        value: response.content,
        hash: requestHash,
        status: 'success',
        code: 200,
      });
    } else {
      setState({
        value: undefined,
        hash: '',
        status: 'error',
        code: response.code,
      });
    }

    return response;
  },
});

export default createDefinedOutcomeSolverSlice;
